import React, { useState } from 'react'
import { Button } from 'primereact/button'
import { Card } from 'primereact/card';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { DataTable } from 'primereact/datatable';
import { useTranslation, Trans } from "react-i18next";
import { Column } from 'primereact/column';
import { Dropdown } from 'primereact/dropdown';

const ImportPreview = ({
    handleBack,
    handleContinue,
    navigateToImportPage,
    filteredRowsNoZero,
    validFilteredRows,
    importBusinessCustomers,
    onSubmitImport,
    defMembership,
    setState,
    state,
    nonDuplicateNumbers,
    duplicateNumbers,
    audienceId,
    audienceName,
    previewHeaderKey = "business_customers.import-preview-header",
}) => {
    const { t } = useTranslation("common");

    const [activeIndex, setActiveIndex] = useState(null);
    const [first, setFirst] = useState(0);
    const [rows, setRows] = useState(10);

    const onPageChange = (e) => {
        const isRowsChanged = rows !== e.rows;
        if (isRowsChanged) {
            setFirst(0);
            setRows(e.rows);
        } else {
            setFirst(e.first);
            setRows(e.rows);
        }
    };

    const handleTabChange = (e) => {
        if (e.index === 0) {
            return;
        }
        setActiveIndex(e.index);
    };

    const convertArrayToCSV = (data) => {
        // Check if data is an array and contains at least one object
        if (!Array.isArray(data) || data.length === 0) {
            return "";
        }
        // Extract headers from the first object's keys
        const headers = Object.keys(data[0]);
        // Map over data to create each row as a CSV string
        const rows = data.map(row =>
            headers.map(header => JSON.stringify(row[header] || "")).join(",")
        );
        // Join headers and rows with newline characters
        return [headers.join(","), ...rows].join("\n");
    };

    const createCSVFile = (csvString) => {
        return new Blob([csvString], { type: 'text/csv' });
    };

    const csvString = convertArrayToCSV(nonDuplicateNumbers);

    // Create a file from the CSV string
    const csvBlob = createCSVFile(csvString);
    const updatedFile = new File([csvBlob], 'nonDuplicateNumbers.csv', { type: 'text/csv' });

    //convert duplicate numbers to a CSV downloadable file
    const downloadCsv = (data) => {
        const sortedData = [...data].sort((a, b) => b["Phone number"].localeCompare(a["Phone number"]));
        const headers = ["Name", "Phone Number", "Reference", "Segment", "Tags"];
        const rows = sortedData.map((item, index) => [
            item.Name,
            item["Phone number"],
            item.Reference,
            item.Segment,
            item.Tags,
        ]);

        const csvContent = [headers, ...rows]
            .map((row) => row.join(","))
            .join("\n");

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);

        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "duplicate_contacts.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const handleImportPreviewContinue = () => {
        if (clearForImport === 0) {
            return;
        }

        const formData = new FormData();
        formData.append("file", updatedFile, updatedFile.name);

        setState((prev) => ({ ...prev, uploading: true }));
        if (onSubmitImport) {
            onSubmitImport(formData);
        } else {
            importBusinessCustomers(defMembership.business.id, audienceId, formData);
        }
        handleContinue();
    }

    const totalDuplicates = duplicateNumbers.length;
    const invalidPhoneNumberImport = filteredRowsNoZero.length;
    const clearForImport = validFilteredRows.length - totalDuplicates;

    const totalEntries = totalDuplicates + invalidPhoneNumberImport + clearForImport;

    const columns = [
        { field: "Name", header: t("common.name") }, { field: "Phone number", header: t("common.phone_no") },
        { field: "Reference", header: t("common.reference") }, { field: "Segment", header: t("common.segment") },
        { field: "Tags", header: t("common.tags") }, { field: "BatchNo", header: t("common.batch_no") }
    ]


    const customTemplate = {
        layout: 'RowsPerPageDropdown FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport',
        RowsPerPageDropdown: (options) => {
            const dropdownOptions = [
                { label: '10', value: 10 },
                { label: '20', value: 20 },
                { label: '50', value: 50 },
                { label: '100', value: 100 },
                { label: '200', value: 200 }
            ];

            return (
                <div className="p-d-flex p-ai-center p-mr-3 p-2" >
                    <span className="" style={{ display: "flex", alignSelf: "center" }}>
                        {t("business_customers.item_labels")}{":"}{' '}
                    </span>
                    <Dropdown
                        value={options.value}
                        options={dropdownOptions}
                        onChange={(e) => {
                            options.onChange(e)
                        }}
                        style={{ width: '70px', minWidth: '70px', border: "1px solid #495057" }}
                        panelClassName="custom-dropdown-panel"
                    />
                </div>
            );
        }
    };

    return (
        <div>
            <Card className='main-upload-card'>
                <div className='pl-4 pt-5'>
                    <p className='title-text mb-0'>{t(previewHeaderKey)}</p>
                    <p className='sub_text mb-5'>{<Trans i18nKey="business_customers.preview_text" components={{ 1: <span className='point-one-black' /> }} />}</p>
                    <div className='import-preview-accordion'>
                        <Accordion activeIndex={activeIndex} multiple onTabChange={handleTabChange} className='mb-3 mt-2'>
                            <AccordionTab
                                header={
                                    <Trans i18nKey="business_customers.accordion_header1"
                                        values={{ value: clearForImport, val: totalEntries }}
                                        components={{ 1: <strong style={{ color: '#000' }} />, 2: <strong style={{ color: '#146C18' }} /> }} />
                                }
                                className='success'>
                            </AccordionTab>

                            {(invalidPhoneNumberImport > 0) && <AccordionTab
                                header={
                                    <div>
                                        <Trans i18nKey={invalidPhoneNumberImport === 1 ? "business_customers.accordion_header2" : "business_customers.accordion_header_2"}
                                            values={{ value: invalidPhoneNumberImport }}
                                            components={{ 1: <strong style={{ color: '#000' }} />, 2: <strong style={{ color: '#d0021b' }} /> }} />
                                        <p className='mb-0 mt-1 accordion-sub'>{t("business_customers.accordion_header_sub")}</p>
                                    </div>
                                }
                            >
                                <DataTable value={filteredRowsNoZero} paginator first={first} rows={rows} totalRecords={invalidPhoneNumberImport}
                                    onPage={onPageChange} className="p-datatable-responsive-table data_table custom-table-style">
                                    {columns.map(({ header, field }, i) => <Column key={i} header={header} field={field} headerClassName='accord'
                                        headerStyle={{
                                            backgroundColor: '#f0f0f0'
                                        }} />
                                    )}
                                </DataTable>
                            </AccordionTab>

                            }
                            {(totalDuplicates > 0) && <AccordionTab
                                header={
                                    <div>
                                        <Trans i18nKey="business_customers.accordion_header3"
                                            values={{ value: totalDuplicates }}
                                            components={{ 1: <strong style={{ color: '#000' }} />, 2: <strong style={{ color: '#d0021b' }} /> }} />
                                        <p className='mb-0 mt-1 accordion-sub'>{t("business_customers.accordion_header_sub")}</p>
                                    </div>
                                }
                            >
                                <section className='flex justify-content-end'>
                                    <div className='export-text my-3 mr-2' onClick={() => downloadCsv(duplicateNumbers)}>
                                        {t("common.export_csv")}
                                    </div>
                                </section>
                                <DataTable value={duplicateNumbers} paginator first={first} rows={rows} totalRecords={totalDuplicates}
                                    onPage={onPageChange} className="p-datatable-responsive-table data_table custom-table-style"
                                    paginatorTemplate={customTemplate} sortField="Phone number" sortOrder={-1}
                                    currentPageReportTemplate="{first} - {last} of {totalRecords}"
                                >
                                    {columns.map(({ header, field }, i) => <Column key={i} header={header} field={field} headerStyle={{
                                        backgroundColor: '#f0f0f0'
                                    }} />
                                    )}
                                </DataTable>
                            </AccordionTab>}
                        </Accordion>
                    </div>

                    {clearForImport === 0 && (
                        <p className="import-text mb-3 reveknew_red">{t("business_customers.import_no_valid_records")}</p>
                    )}

                    <div className='buttons-cont'>
                        <div className='back-btn mt-6'>
                            <Button className='import_back_button' onClick={handleBack} >
                                {t("Back")}
                            </Button>
                        </div>
                        <div className='cont-btn'>
                            <Button className='import_cancel_button' onClick={navigateToImportPage} >
                                {t("cancel")}
                            </Button>
                            <Button
                                className='import_button steps-button py-2'
                                onClick={handleImportPreviewContinue}
                                disabled={clearForImport === 0}
                            >
                                {t("business_customers.preview-button")}
                            </Button>
                        </div>
                    </div>

                </div>
            </Card >
        </div >
    )
}

export default ImportPreview